import { Bar } from "react-chartjs-2";
import { Chart as ChartJS, registerables } from "chart.js";
import moment from "moment";
import { intl } from "LocaleWrapper";
import "chartjs-adapter-moment";

import { DateUtils } from "utils/DateUtils";
import { getShipDayTimeWindows } from "view/ships/utils/ShipUtils";

import { ShipEventType } from "__generated__/enums";
import { ShipEventsEventReportsForPeriodFragment } from "__generated__/view/ships/graphql/ship.fragments.generated";

ChartJS.register(...registerables);

type Reports = ShipEventsEventReportsForPeriodFragment["reports"];

const ShipEventsVerticalBar = ({ timeWindow, reports }: {
	timeWindow: {
		from: number,
		to: number,
	},
	reports: Reports
}) => {

	const labels = getShipDayTimeWindows(timeWindow.from, timeWindow.to);

	const buildReportBlock = (report: Reports[number], type: ShipEventType) => {

		const blocks: { y: [number, number ], x: number }[] = [];

		report.events.map(e => ({ ...e, ship: report.ship })).filter(event => event.type === type).forEach(shipEvent => {

			const sameDate = moment(shipEvent.start).isSame(moment(shipEvent.end), "day");

			if (sameDate) {

				const startDay = DateUtils.getTimestamp(moment(shipEvent.start).startOf("day"));
				const start = moment(shipEvent.start).diff(moment(startDay), "minutes");
				const diff = moment(shipEvent.end).diff(moment(shipEvent.start), "minutes");

				blocks.push({
					y: [ start, start + diff ],
					x: startDay || 0,
				});

			} else {
				const days = moment(shipEvent.end).diff(moment(shipEvent.start), "days") + 2;

				Array.from(Array(days).keys()).forEach((e, index) => {

					if (index === 0) {

						const startDay = DateUtils.getTimestamp(moment(shipEvent.start).startOf("day"));
						const start = moment(shipEvent.start).diff(moment(startDay), "minutes");
						const diff = moment(shipEvent.start).endOf("day").diff(moment(shipEvent.start), "minutes");

						blocks.push({
							y: [ start, start + diff ],
							x: startDay || 0,
						});
					} else if ((index + 1) === days) {

						const startDay = DateUtils.getTimestamp(moment(shipEvent.end).startOf("day"));
						const diff = moment(shipEvent.end).diff(moment(shipEvent.end).startOf("day"), "minutes");

						blocks.push({
							y: [ 0, diff ],
							x: startDay || 0,
						});
					} else {

						const startDay = DateUtils.getTimestamp(moment(shipEvent.start).add(index, "days").startOf("day"));

						blocks.push({
							y: [ 0, 1440 ],
							x: startDay || 0,
						});
					}
				});
			}
		});

		return blocks;
	};

	return (
		<div style={{ display: "flex", overflowX: "auto" }}>
			{reports.map((report) => (
				<div style={{ padding: 16, width: reports.length > 1 ? 600 : "100%", flex: reports.length > 1 ? "0 0 600px" : "unset" }} key={report.ship.id}>
					<Bar
						height="350px"
						width="100%"
						data={{
							labels: labels,
							datasets: [ {
								label: intl.formatMessage({ id: "TERMINAL" }),
								backgroundColor: "rgb(243, 205, 231)",
								data: buildReportBlock(report, ShipEventType.LoadDischarge),
							}, {
								label: intl.formatMessage({ id: "SHIP_EVENT_RESTING" }),
								backgroundColor: "rgb(204, 66, 83)",
								data: buildReportBlock(report, ShipEventType.Resting),
							}, {
								label: intl.formatMessage({ id: "SHIP_EVENT_SAILING" }),
								backgroundColor: "rgb(40, 156, 55)",
								data: buildReportBlock(report, ShipEventType.Sailing),
							}, {
								label: intl.formatMessage({ id: "SHIP_EVENT_WAITING" }),
								backgroundColor: "rgb(49, 144, 216)",
								data: buildReportBlock(report, ShipEventType.Waiting),
							} ],
						}}
						options={{
							maintainAspectRatio: false,
							plugins: {
								title: {
									display: true,
									text: report.ship.name,
								},
								tooltip: {
									callbacks: {
										title: () => report.ship.name,
										label: (item) => {
											return [
												item.dataset.label || "",
												`${moment(item.parsed.x).add(item.parsed._custom?.min, "minutes").format("lll")} - ${moment(item.parsed.x).add(item.parsed._custom?.max, "minutes").format("lll")}`,
											];
										},
									},
								},
							},
							responsive: true,
							scales: {
								x: {
									stacked: true,
									type: "time",
									time: {
										unit: "day",
										displayFormats: {
											day: "MMM D",
										},
									},
									min: timeWindow.from,
									max: DateUtils.getTimestamp(moment(timeWindow.to).startOf("day"))!,
									ticks: {
										autoSkip: false,
									},
								},
								y: {
									stacked: false,
									min: 0,
									max: 1440,
								},
							},
						}}
					/>
				</div>
			))}
		</div>
	);
};

export default ShipEventsVerticalBar;
